<script lang="ts">
  import 'swiper/css/bundle';
  import 'swiper/css/navigation';
  import { t } from 'svelte-i18n';
  import Button from './button.svelte';
  import { getContext, onMount } from 'svelte';
  import { trackEvent } from './utils';
  import { Navigation } from 'swiper';
  import { Swiper, SwiperSlide } from 'swiper/svelte';
  import ContactFormOption from './contact-form-option.svelte';
  import { setInitialHeaderColors } from './header-store';

  type FormInput = { value: string; error: boolean; focused: boolean; };

  let emailInput: HTMLInputElement;
  let ideaInput: HTMLTextAreaElement;
  let swiper;
  let ideaInputmode: 'none' | 'text' = 'text';
  let currentSlide = 0;
  const { closeModal } = getContext<{ closeModal: () => void }>('modal');
  let form: { name: FormInput; email: FormInput; idea: FormInput; } = { 
    name: {
      value: '',
      error: false,
      focused: false,
    }, 
    email: {
      value: '',
      error: false,
      focused: false,
    },  
    idea: {
      value: '',
      error: false,
      focused: false,
    }, 
  };
  let serviceSelectedOptions = [];
  let serviceOptions = [
    'MODAL.EMAIL.SERVICE.OPTION1',
    'MODAL.EMAIL.SERVICE.OPTION2',
    'MODAL.EMAIL.SERVICE.OPTION3',
    'MODAL.EMAIL.SERVICE.OPTION4',
    'MODAL.EMAIL.SERVICE.OPTION5',
  ];
  let urgencySelectedOption = [];
  let urgencyOptions = [
    'MODAL.EMAIL.URGENCY.OPTION1',
    'MODAL.EMAIL.URGENCY.OPTION2',
    'MODAL.EMAIL.URGENCY.OPTION3',
  ];
  let budgetSelectedOption = [];
  let budgetOptions = [
    'MODAL.EMAIL.BUDGET.OPTION1',
    'MODAL.EMAIL.BUDGET.OPTION2',
    'MODAL.EMAIL.BUDGET.OPTION3',
  ];
  $: optionsSelected = budgetSelectedOption.length > 0 && urgencySelectedOption.length > 0 && serviceSelectedOptions.length > 0;
  $: isDisabled = (form.email.error || form.name.error) || (!form.email.value || !form.name.value);

  onMount(() => {
    // change height depending on the value of the textarea
    ideaInput.addEventListener('input', () => {
      ideaInput.style.height = `${calcHeight(ideaInput.value)}px`;
    });
  })
  const accessKey = 'b8d5b1f7-c017-47fd-b660-b1dbf22ae70a';
  
  let loading = false;
  let submitErrorMessage = '';

  function validateProperty(event) {
    if (!event?.target?.value) {
      addError(event.target.name)
      return;
    }

    removeError(event.target.name);
  }

  function addError(formField: 'name' | 'email' | 'idea') {
    if (formField === 'idea') {
      return;
    }

    form = {
      ...form,
      [formField]: {
        ...form[formField],
        error: true,
      },
    };
  }

  function removeError(formField: 'name' | 'email' | 'idea') {
    form = {
      ...form,
      [formField]: {
        ...form[formField],
        error: false,
      },
    };
  }

  async function submitForm(e: MouseEvent) {
    e.preventDefault();

    if (isDisabled || !optionsSelected) {
      return;
    }

    loading = true;

    cancelInputMode();

    const serviceMessage = serviceSelectedOptions.map((value) => $t(value)).join(', ');
    const urgencyMessage = urgencySelectedOption.map((value) => $t(value)).join(', ');
    const budgetMessage = budgetSelectedOption.map((value) => $t(value)).join(', ');
    const message = `Services: ${serviceMessage} 
    Urgency: ${urgencyMessage}
    Budget: ${budgetMessage}
    ${form.idea.value ? `Message: ${form.idea.value}` : '' }`

    const payload = {
      access_key: accessKey,
      name: form.name.value,
      email: form.email.value,
      message: message,
    };
    const json = JSON.stringify(payload);

    const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
        },
        body: json,
    });

    const result = await response.json();
    if (result.success) {
      trackEvent('submit_form', 'engagement', 'contact_form');
      slideNext();
    } else {
      submitErrorMessage = result.message;
    }
    
    loading = false;
  }

  function setFocus(event) {
    const formField = event.target.name;
    
    form = {
      ...form,
      [formField]: {
        ...form[formField],
        focused: true,
      },
    };
  }

  function setBlur(event) {
    const formField = event.target.name;
    
    form = {
      ...form,
      [formField]: {
        ...form[formField],
        focused: false,
      },
    };
  }

  function handleKeyPress(event) {
    const { name } = event.target;

    if (event.key !== "Enter" || event.keyCode !== 13 || name === 'idea'){
      return;
    }

    event.preventDefault();

    if (name === 'name') {
      emailInput.focus();
      return;
    }

    if (name === 'email') {
      ideaInput.focus();
      return;
    }
  }

  function cancelInputMode() {
    if (ideaInput.inputMode === 'none') {
      return;
    }

    ideaInputmode = 'none';
    setTimeout(() => {
      ideaInputmode = 'text';
    }, 50);
  }

  /**
   * Calculates the height of the input based on the number of line breaks
   * @param value
   */
  function calcHeight(value) {
    // find the number of line breaks, including new lines when pressing enter
    const numLineBreaks = (value.match(/\n/g) || []).length;
    // min-height + lines x line-height + padding + border
    let newHeight = 40 + numLineBreaks * 24 + 16 + 1;
    return newHeight;
  }

  function slideNext() {
    if (currentSlide === 0) {
      trackEvent('slide_to_form', 'engagement', 'contact_form');
    }

    if (currentSlide === 1) {
      trackEvent('slide_to_conclusion', 'engagement', 'contact_form');
    }

    swiper.slideNext();
  }

  function slidePrevious() {
    if (currentSlide === 1) {
      trackEvent('slide_to_options', 'engagement', 'contact_form');
    }
    
    swiper.slidePrev();
  }

  function goToAboutPage() {
    closeModal();
    window.scrollTo(0, 0);
    setInitialHeaderColors('white', 'blue');
  }

  function scrollToTop() {
    const modal = document.querySelector('#modal-container');

    modal.scrollTo({ top: 0, behavior: 'smooth' });
  }
</script>

<div aria-hidden="true" class="c-form__decoration"></div>
<section class="relative flex flex-col justify-center px-8 xs:px-[60px] sm:px-[88px] md:px-0 md:max-w-[424px] md2:max-w-[787px] lg:max-w-[960px] xl:max-w-[1114px] md:mx-auto">
  <div class:hidden={currentSlide === 2} class="flex gap-2 mx-auto md2:mx-0 mb-16">
    <span class={`h-1 w-10 rounded-[2px] transition-all duration-200 ease-in ${currentSlide === 0 ? 'bg-yellow-light opacity-100' : 'bg-white opacity-[0.4]'}`}></span>
    <span class={`h-1 w-10 rounded-[2px] transition-all duration-200 ease-in ${currentSlide === 1 ? 'bg-yellow-light opacity-100' : 'bg-white opacity-[0.4]'}`}></span>
  </div>
  <div class="h-full w-full">
    <Swiper
      modules={[Navigation]}
      allowTouchMove={false}
      autoHeight={true}
      on:activeIndexChange={(e) => {
        const swiper = e.detail[0];
        currentSlide = swiper.activeIndex;
      }}
      on:slideChangeTransitionEnd={scrollToTop}
      on:swiper={(e) => {
        swiper = e.detail[0];
        trackEvent('open_form', 'engagement', 'contact_form');
        
        currentSlide = e.detail[0].activeIndex;
      }}
    >
      <SwiperSlide>
        <h1 class="c-form__title text-white selection:bg-text-dark selection:text-blue leading-snug">
          {$t('MODAL.EMAIL.TITLE1')}
        </h1>
        <div class="my-16">
          <ContactFormOption label="MODAL.EMAIL.SERVICE.TITLE" multiple={true} bind:selectedOptions={serviceSelectedOptions} options={serviceOptions}></ContactFormOption>
          <div class="c-form__divider"></div>
          <ContactFormOption label="MODAL.EMAIL.URGENCY.TITLE" bind:selectedOptions={urgencySelectedOption} options={urgencyOptions}></ContactFormOption>
          <div class="c-form__divider"></div>
          <ContactFormOption label="MODAL.EMAIL.BUDGET.TITLE" bind:selectedOptions={budgetSelectedOption} options={budgetOptions}></ContactFormOption>
        </div>
        <div class="flex flex-col items-center md2:items-start pb-40 md:pb-24 md2:pb-32">
          <Button on:click={() => slideNext()} short={true} disabled={!optionsSelected} variant='yellow'>{$t('MODAL.EMAIL.NEXT')}</Button>
        </div>
      </SwiperSlide>
      <SwiperSlide>
        <h1 class="c-form__title c-form__title--left">
          {$t('MODAL.EMAIL.TITLE2')}
        </h1>
        <p class="c-form__strong leading-normal pt-2 md2:pt-4 pb-[40px] md2:pb-16 text-white opacity-[0.6] text-base sm:text-xxs md2:text-xs font-light">
          {@html $t('MODAL.EMAIL.SUBTITLE2')}
        </p>
        <form name="contact-form">
          <div class="c-form__input-container mb-[2.5rem] mb-[2.5rem] md:mb-16">
            <label class="c-form__label { form.name.focused ? 'text-yellow opacity-100' : 'text-white opacity-50' }" for="name">{$t('MODAL.EMAIL.NAME_LABEL')}</label>
            <div class:hidden={!form.name.error} class="flex items-center mt-4">
              <ion-icon name="alert-circle-outline" class="mr-[6px] text-red text-base"></ion-icon>
              <p class="text-red p-0 leading-1 text-[0.875rem] md:text-base">{$t('MODAL.EMAIL.NAME_ERROR')}</p>
            </div>
            <input class="c-form__input border-0 border-b border-b-white/50" on:keypress={handleKeyPress} on:focus={setFocus} on:blur={setBlur} class:border-b-red={form.name.error} name="name" type="text" bind:value={form.name.value} placeholder="{$t('MODAL.EMAIL.NAME_PLACEHOLDER')}" on:input={validateProperty}>
          </div>
          <div class="c-form__input-container mb-[2.5rem] md:mb-16">
            <label class="c-form__label { form.email.focused ? 'text-yellow opacity-100' : 'text-white opacity-50' }" for="email">{$t('MODAL.EMAIL.EMAIL_LABEL')}</label>
            <div class:hidden={!form.email.error} class="flex items-center mt-4">
              <ion-icon name="alert-circle-outline" class="mr-[6px] text-red text-base"></ion-icon>
              <p class="text-red p-0 leading-1 text-[0.875rem] md:text-base">{$t('MODAL.EMAIL.EMAIL_ERROR')}</p>
            </div>
            <input class="c-form__input border-0 border-b border-b-white/50" bind:this={emailInput} on:keypress={handleKeyPress} on:focus={setFocus} on:blur={setBlur} class:border-b-red={form.email.error} name="email" type="text" bind:value={form.email.value} placeholder="{$t('MODAL.EMAIL.EMAIL_PLACEHOLDER')}" on:input={validateProperty}>
          </div>
          <div class="c-form__input-container">
            <label class="c-form__label { form.idea.focused ? 'text-yellow opacity-100' : 'text-white opacity-50' }" for="idea">{$t('MODAL.EMAIL.IDEA_LABEL')} <p class="text-base text-white p-0 ml-2 opacity-[0.6]">{$t('MODAL.EMAIL.OPTIONAL')}</p></label>
            <textarea inputmode={ideaInputmode} class="resize-ta c-form__input border-0 border-b border-b-white/50 h-16 md2:h-10 max-h-72" bind:this={ideaInput} on:keypress={handleKeyPress} on:focus={setFocus} on:blur={setBlur} name="idea" bind:value={form.idea.value} placeholder="{$t('MODAL.EMAIL.IDEA_PLACEHOLDER')}" on:input={validateProperty}></textarea>
          </div>
        </form>
        <div class="flex flex-col items-center md2:items-start mt-12 md:mt-[5.5rem] md2:mt-[4.75rem] pb-40 md:pb-24 md2:pb-32">
          <div class="flex flex-row gap-6">
            <Button on:click={slidePrevious} short={true} variant='clear-white'>{$t('MODAL.EMAIL.PREVIOUS')}</Button>
            <Button on:click={submitForm} short={true} variant="yellow" disabled={isDisabled} loading={loading}>{$t('MODAL.EMAIL.SUBMIT')}</Button>
          </div>
          <div class:hidden={!submitErrorMessage} class="flex items-start mt-6">
            <ion-icon name="alert-circle-outline" class="mr-[6px] text-red text-base mt-1"></ion-icon>
            <p class="text-red p-0 leading-1 text-[0.875rem] md:text-base">{$t('MODAL.EMAIL.FORM_ERROR')}</p>
          </div>
        </div>
      </SwiperSlide>
      <SwiperSlide>
        <div class="flex flex-col items-center justify-center min-h-[calc(100vh-160px)] sm:min-h-[calc(100vh-208px)] md2:min-h-[calc(100vh-160px)] lg:min-h-[calc(100vh-192px)] xl:min-h-[calc(100vh-240px)]">
          <img src="../images/checkmark-contact-form.svg" alt="checkmark" class="h-[80px] w-[80px]">
          <h1 class="text-xs md2:text-md p-0 pt-6 pb-2 text-white opacity-[0.9] font-medium leading-snug">
            {$t('MODAL.EMAIL.SUCCESS.TITLE')}
          </h1>
          <p class="text-base text-center md2:text-xs text-white opacity-[0.9] leading-snug">
            {$t('MODAL.EMAIL.SUCCESS.SUBTITLE')}
          </p>
          <p class="text-base text-center p-0 pb-[40px] text-white opacity-[0.7] leading-snug">
            {$t('MODAL.EMAIL.SUCCESS.LABEL')}
          </p>
          <a href="/about">
            <Button on:click={goToAboutPage} short={true} variant="yellow">{$t('MODAL.EMAIL.SUCCESS.KNOW_MORE')}</Button>
          </a>
        </div>
      </SwiperSlide>
    </Swiper>  
  </div>
</section>

<style global lang="scss">
  .c-form {
    height: 100%;
    &__decoration {
      position: fixed;
      bottom: 0;
      left: 0;
      height: 16px;
      width: 100%;
      padding-bottom: env(safe-inset-area-bottom);
      background: url('../images/contact-form-dec.svg');
      z-index: 1001;
    }

    &__divider {
      @apply h-[1px] bg-white opacity-[0.3] my-[40px] md2:my-[48px];
    }

    &__title {
      @apply text-white selection:bg-text-dark selection:text-blue text-center p-0 md2:text-left leading-snug opacity-[0.9];
      @apply text-xxs xs:text-xs sm:text-sm md2:text-md;

      &--left {
        @apply text-left;
      }
    }

    &__input {
      @apply bg-transparent pb-4 mt-6 xl:mt-8 text-white selection:bg-text-dark selection:text-blue;
      @apply text-[0.875rem] xs:text-base md:text-xxs md2:text-xs;

      -webkit-tap-highlight-color: transparent;
    }
    
    &__input-container {
      @apply flex flex-col;
    }

    &__label {
      @apply text-[0.875rem] xs:text-base md:text-xxs md2:text-xs flex items-center leading-snug;
    }
  }
  
  
  .c-form__strong {
      strong {
        font-weight: 600;
      }
    }

  .resize-ta {
    min-height: 100px;

    &::-webkit-scrollbar {
    width: 4px;
    border-radius: 3px;
    }

    &::-webkit-scrollbar-button {
      display: none;
    }

    &::-webkit-scrollbar-track {
      background-color: transparent;
    }

    &::-webkit-scrollbar-thumb {
      background: #d2d2d2; 
      border-radius: 3px;
    }

    @media screen and (max-height: 668px){
      min-height: 72px;
    }
  }
</style>